import { AllLanguages, Language, LanguageIdLanguages, LanguageIds } from "../language"
import { DropdownData, IdLookup } from "../Models/system"
import { DropdownItem } from "../Components/Dropdown"
import { WorkshopEvent } from "../Models/workshops"
import { assertUnreachable, NullableDate, NullableDateRange } from "../utility"
import { isEventInTimeRange } from '../Models/logic'
import { Config } from "../constants"

// some filter selections can affect others' values and/or ranges
// we handle this with a reducer that operates on this state

export interface FilterDomains {
	// constants & ALL filter options
	workshopEvents: Array<WorkshopEvent>
	allLanguages: Set<Language>

	allWorkshopTypeMenuItems: Array<DropdownItem<number>>
	allLocationTier1MenuItems: Array<DropdownItem<number>>
	allLocationTier2MenuItems: Array<DropdownItem<number>>

	locationTier2ParentIds: IdLookup
}

export interface FilterRanges {
	// AVAILABLE filter options
	filteredWorkshopEvents: Array<WorkshopEvent>
	availableLanguages: Set<Language>
	workshopTypeMenuItems: Array<DropdownItem<number>>
	locationTier1MenuItems: Array<DropdownItem<number>>
	locationTier2MenuItems: Array<DropdownItem<number>>
	availableDateRange: NullableDateRange
}

export interface FilterSelections {
	// SELECTED filter options
	languages: Set<Language>
	workshopTypeId?: number
	startDate: NullableDate
	endDate: NullableDate
	locationTier1Id?: number
	locationTier2Id?: number
	userHasFiltered: boolean
}

export interface FilterState {
	domains: FilterDomains
	selections: FilterSelections
	ranges: FilterRanges
}

export function getWorkshopEventFilter(selections: FilterSelections): (event: WorkshopEvent) => boolean {
	const threshold = new Date()
	threshold.setHours(threshold.getHours() + Config.HoursUntilRegistrationCutoff)

	const startDate = selections.startDate && selections.startDate > threshold ? 
		selections.startDate : 
		threshold

	let endDate = selections.endDate
	if (selections.endDate) {
		endDate = new Date(selections.endDate)
		endDate.setHours(23)
		endDate.setMinutes(59)
		endDate.setSeconds(59)
		endDate.setMilliseconds(999)
	}

	return function (workshopEvent: WorkshopEvent) {
		const selectedLanguagesIds = new Set(Array.from(selections.languages).map(l => LanguageIds[l]))
		const passesFilter = selectedLanguagesIds.has(workshopEvent.LanguageId) &&
			isEventInTimeRange(workshopEvent, [startDate, endDate]) &&
			(!selections.workshopTypeId || workshopEvent.WorkshopTypeId == selections.workshopTypeId) &&
			(!selections.locationTier1Id || workshopEvent.Location.MunicipalityTier1Id == selections.locationTier1Id) &&
			(!selections.locationTier2Id || workshopEvent.Location.MunicipalityTier2Id == selections.locationTier2Id)

		return passesFilter
	}
}


export interface ConstrictedDropdown {
	constrictedItems: Array<DropdownItem<number>>
	constrictedId?: number
}

// NOTE: this method is only required because online workshops also have a physical location corresponding with a guelph, wellington webinar
// function getLocationTier1Data(filteredWorkshopEvents: Array<WorkshopEvent>, domains: FilterDomains, workshopTypeId: number): ConstrictedDropdown {
function getLocationTier1Data(domains: FilterDomains, workshopTypeId: number): ConstrictedDropdown {

	const constrictedDropdown: ConstrictedDropdown = {
		constrictedItems: []
	}

	if (workshopTypeId) {
		const availableIds = new Set<number>()

		domains.workshopEvents.filter(we => we.WorkshopTypeId == workshopTypeId).forEach(e => {
			availableIds.add(e.Location.MunicipalityTier1Id)
		})

		constrictedDropdown.constrictedItems = domains.allLocationTier1MenuItems.filter(ddi => availableIds.has(ddi.value))
		constrictedDropdown.constrictedId = constrictedDropdown.constrictedItems.length == 1 ? constrictedDropdown.constrictedItems[0].value : undefined
	}

	return constrictedDropdown
}



// we may actually need to generalize this to tier1 and workshop type as well... TODO: investigate
// better yet... roll into a generalized initSelectionAndRanges function, which can be reused
// function getLocationTier2Data(filteredWorkshopEvents: Array<WorkshopEvent>, domains: FilterDomains, locationTier1Id: number): ConstrictedDropdown {
function getLocationTier2Data(domains: FilterDomains, locationTier1Id: number): ConstrictedDropdown {

	const constrictedDropdown: ConstrictedDropdown = {
		constrictedItems: []
	}

	if (locationTier1Id) {
		const availableIds = new Set<number>()

		domains.workshopEvents.forEach(e => {
			availableIds.add(e.Location.MunicipalityTier2Id)
		})

		constrictedDropdown.constrictedItems = domains.allLocationTier2MenuItems.filter(ddi => domains.locationTier2ParentIds[ddi.value] == locationTier1Id && availableIds.has(ddi.value))
		constrictedDropdown.constrictedId = constrictedDropdown.constrictedItems.length == 1 ? constrictedDropdown.constrictedItems[0].value : undefined
	}

	return constrictedDropdown
}


// We constrict available ranges when we move to a new workshop (with new ranges of input options)
export function initState(filteredWorkshopEvents: Array<WorkshopEvent>, domains: FilterDomains): FilterState {

	const availableLanguages = new Set<Language>()

	const workshopTypeIds = new Set<number>()
	const availableTier1Ids = new Set<number>()
	const availableTier2Ids = new Set<number>()
	let minDate = new Date(9999, 12, 31)
	let maxDate = new Date(1, 1, 1)

	filteredWorkshopEvents.forEach(e => {
		availableLanguages.add(LanguageIdLanguages[e.LanguageId])
		workshopTypeIds.add(e.WorkshopTypeId)
		availableTier1Ids.add(e.Location.MunicipalityTier1Id)
		availableTier2Ids.add(e.Location.MunicipalityTier2Id)
		if (e.Schedule[0].StartDateAndTime < minDate) minDate = e.Schedule[0].StartDateAndTime
		if (e.Schedule[e.Schedule.length - 1].EndDateAndTime > maxDate) maxDate = e.Schedule[e.Schedule.length - 1].EndDateAndTime
	})

	const workshopTypeMenuItems = domains.allWorkshopTypeMenuItems.filter(ddi => workshopTypeIds.has(ddi.value))
	const workshopTypeId = workshopTypeMenuItems.length == 1 ? workshopTypeMenuItems[0].value : undefined

	const locationTier1MenuItems = domains.allLocationTier1MenuItems.filter(ddi => availableTier1Ids.has(ddi.value))
	const locationTier1Id = locationTier1MenuItems.length == 1 ? locationTier1MenuItems[0].value : undefined

	const locationTier2MenuItems = domains.allLocationTier2MenuItems.filter(ddi => domains.locationTier2ParentIds[ddi.value] == locationTier1Id && availableTier2Ids.has(ddi.value))
	const locationTier2Id = locationTier2MenuItems.length == 1 ? locationTier2MenuItems[0].value : undefined

	const filterState: FilterState = {
		domains, 
		ranges: {
			filteredWorkshopEvents,
			availableLanguages,
			workshopTypeMenuItems,
			availableDateRange: [new Date(), maxDate],
			locationTier1MenuItems,
			locationTier2MenuItems
		},
		selections: {
			languages: availableLanguages,
			startDate: new Date(),
			endDate: null,
			locationTier1Id,
			locationTier2Id,
			workshopTypeId,
			userHasFiltered: false
		},
	}

	return filterState
}

export type DateChangeActionType = 'StartDateChange' | 'EndDateChange'
export interface DateChangeAction {
	type: DateChangeActionType
	date: NullableDate
}

export type IdChangeActionType = 'WorkshopTypeIdChange' | 'LocationTier1IdChange' | 'LocationTier2IdChange'
export interface IdChangeAction {
	type: IdChangeActionType
	Id: number | undefined
}

export interface SelectedLanguagesChangeAction {
	type: 'SelectedLanguagesChange'
	languages: Set<Language>
}

export interface GuiLanguageChangeAction {
	type: 'GuiLanguageChange'
	dropdownData: DropdownData
}

export interface WorkshopChangeAction {
	type: 'WorkshopChange'
	seed: FilterSeed
}

// for cases such as registration status and attendance count changes
export interface WorkshopUpdateAction {
	type: 'WorkshopUpdate'
	updatedWorkshopEvents: Array<WorkshopEvent>
}

// for filtering out workshops that have passed their cutoff
export interface FilterEventsAction {
	type: 'FilterEvents'
}

export type FilterAction = DateChangeAction | IdChangeAction | SelectedLanguagesChangeAction | WorkshopChangeAction | GuiLanguageChangeAction | WorkshopUpdateAction | FilterEventsAction

export function isFilterInputChangeAction(action: FilterAction) {
	switch(action.type) {
		case 'StartDateChange': 
		case 'EndDateChange': 
		case 'WorkshopTypeIdChange': 
		case 'LocationTier1IdChange': 
		case 'LocationTier2IdChange': 
		case 'SelectedLanguagesChange': 
			return true
		case 'GuiLanguageChange':
		case 'WorkshopChange':
		case 'WorkshopUpdate': 
		case 'FilterEvents':
			return false
		default: 
			assertUnreachable(action)
	}
}

export interface FilterSeed {
	workshopEvents: Array<WorkshopEvent>
	dropdownData: DropdownData
}

// This is brutal.  TODO: come up with a cleaner way to handle langauge switching
export function swapOutDropdownItems(currentState: FilterState, dropdownData: DropdownData) {
	const newState = Object.assign({}, currentState)

	// TODO: language changes may still be messing something up with dropdowns.  Investigate further.
	newState.domains = Object.assign({}, newState.domains)

	newState.domains.allLocationTier1MenuItems = dropdownData.locationTier1
	newState.domains.allLocationTier2MenuItems = dropdownData.locationTier2
	newState.domains.allWorkshopTypeMenuItems = dropdownData.workshopType

	newState.ranges = Object.assign({}, newState.ranges)
	newState.ranges.locationTier1MenuItems = newState.ranges.locationTier1MenuItems.map(ddi => newState.domains.allLocationTier1MenuItems.find(ddiNew => ddiNew.value == ddi.value)!)
	newState.ranges.locationTier2MenuItems = newState.ranges.locationTier2MenuItems.map(ddi => newState.domains.allLocationTier2MenuItems.find(ddiNew => ddiNew.value == ddi.value)!)
	newState.ranges.workshopTypeMenuItems = newState.ranges.workshopTypeMenuItems.map(ddi => newState.domains.allWorkshopTypeMenuItems.find(ddiNew => ddiNew.value == ddi.value)!)

	return newState
}

export function initFilterState(seed: FilterSeed): FilterState {

	const domains: FilterDomains = {
		workshopEvents: seed.workshopEvents,
		allLanguages: AllLanguages,
		allWorkshopTypeMenuItems: seed.dropdownData.workshopType,
		allLocationTier1MenuItems: seed.dropdownData.locationTier1,
		allLocationTier2MenuItems: seed.dropdownData.locationTier2,
		locationTier2ParentIds: seed.dropdownData.locationTier2ParentIds,
	}

	let state = initState(seed.workshopEvents, domains)
	// we do an initial pre-filter here in case api returns some workshops out of our expected range
	const filteredWorkshopEvents = seed.workshopEvents.filter(getWorkshopEventFilter(state.selections))
	let { selections, ranges } = initState(filteredWorkshopEvents, domains)

	const initialState: FilterState = Object.assign({}, {
		domains,
		selections,
		ranges
	})

	return initialState
}

export function reduceFilterState(state: FilterState, action: FilterAction): FilterState {
	let { domains, selections, ranges } = state
	selections = Object.assign({}, selections)
	ranges = Object.assign({}, ranges)

	switch (action.type) {
		case 'SelectedLanguagesChange':
			selections.languages = action.languages 
			break

		case 'StartDateChange':
			selections.startDate = action.date
			if (selections.startDate && selections.endDate && selections.startDate > selections.endDate) selections.endDate = action.date
			break

		case 'EndDateChange':
			selections.endDate = action.date
			if (selections.startDate && selections.endDate && selections.startDate > selections.endDate) selections.startDate = action.date
			break

		case 'WorkshopTypeIdChange':
			selections.workshopTypeId = action.Id
			selections.locationTier1Id = undefined
			selections.locationTier2Id = undefined
			break

		case 'LocationTier1IdChange':
			selections.locationTier1Id = action.Id
			selections.locationTier2Id = undefined
			break

		case 'LocationTier2IdChange':
			selections.locationTier2Id = action.Id
			break

		case 'WorkshopChange':
			const newState = initFilterState(action.seed)
			return newState

		case 'GuiLanguageChange': 
			return swapOutDropdownItems(state, action.dropdownData)

		case 'WorkshopUpdate': 
			domains = Object.assign({}, domains)
			domains.workshopEvents = action.updatedWorkshopEvents
			ranges.filteredWorkshopEvents = domains.workshopEvents.filter(getWorkshopEventFilter(selections))
			return { domains, ranges, selections }

		case 'FilterEvents': 
			ranges.filteredWorkshopEvents = domains.workshopEvents.filter(getWorkshopEventFilter(selections))
			return { domains, ranges, selections }

		default: 
			return assertUnreachable(action);

	}

	if (isFilterInputChangeAction(action)) {
		selections.userHasFiltered = true
		ranges.filteredWorkshopEvents = domains.workshopEvents.filter(getWorkshopEventFilter(selections))
	}

	// this is very messy... TODO: refactor here or elsewhere in logic
	if (selections.workshopTypeId) {

		// const filteredWorkshopEvents = state.domains.workshopEvents.filter(getWorkshopEventFilter(selections))
		// const tier1Data = getLocationTier1Data(filteredWorkshopEvents, state.domains, selections.workshopTypeId)

		// const tier1Data = getLocationTier1Data(domains.workshopEvents, state.domains, selections.workshopTypeId)
		const tier1Data = getLocationTier1Data(state.domains, selections.workshopTypeId)

		if (tier1Data.constrictedId) {
			selections.locationTier1Id = tier1Data.constrictedId
			ranges.locationTier1MenuItems = tier1Data.constrictedItems
		}

		if (selections.locationTier1Id) {
			// const tier2Data = getLocationTier2Data(filteredWorkshopEvents, state.domains, selections.locationTier1Id)
			const tier2Data = getLocationTier2Data(state.domains, selections.locationTier1Id)

			if (tier2Data.constrictedId) {
				selections.locationTier2Id = tier2Data.constrictedId
				ranges.locationTier2MenuItems = tier2Data.constrictedItems
			}
		}
	}

	return {
		domains, 
		selections,
		ranges
	}
}